import React from 'react';
import { Typography } from '@mui/material';
import TaskAlt from '@mui/icons-material/TaskAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

/**
 * Text-label style product selector for subtle display.
 * Use ProductSelectorButton for prominent button-style display.
 *
 * Behavior:
 * - Multiple products: Shows clickable label with expand icon
 * - Single product: Shows non-clickable label (no expand icon)
 * - No products: Renders nothing
 */
const ProductSelectorLabel = React.forwardRef(function ProductSelectorLabel(
  { onClick, product, products, sx, ...props },
  ref,
) {
  const { t } = useTranslation();

  // No products - render nothing
  if (!products || products.length === 0) {
    return null;
  }

  // Single product - show non-clickable label
  if (products.length === 1) {
    return (
      <Typography
        ref={ref}
        component="span"
        variant="body2"
        sx={{
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          ...sx,
        }}
        {...props}
      >
        <TaskAlt sx={{ fontSize: 16 }} />
        {product?.name || products[0]?.name}
      </Typography>
    );
  }

  // Multiple products - show clickable label with expand icon
  return (
    <Typography
      ref={ref}
      component="span"
      variant="body2"
      onClick={onClick}
      sx={{
        color: 'text.secondary',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        '&:hover': {
          textDecoration: 'underline',
        },
        ...sx,
      }}
      {...props}
    >
      <TaskAlt sx={{ fontSize: 16 }} />
      {product?.name || t('product_selection_headline')}
      <ExpandMoreIcon sx={{ fontSize: 14 }} />
    </Typography>
  );
});

export default ProductSelectorLabel;
